import React, { useEffect, useState } from 'react';
import { Tab, Tabs } from 'baseui/tabs';
import { PageTitle } from '../../Shared';
import { Overrides } from '../../Users/baseuiOverrides';
import { BeaconViewKind } from '../../../graphQL';
import { BeaconTab } from '../types';
import { DashboardFrame } from './DashboardFrame';
import { nativeBeaconViews } from './nativeBeaconViews';

type BeaconTabsViewProps = {
  tabs: BeaconTab[];
  activeTabKey: string;
  onTabChange: (tabKey: string) => void;
};

// Exhaustiveness guard: an unhandled tab kind is a compile error, and renders null at runtime.
const assertNever = (_kind: never): null => null;

const renderTabContent = (tab: BeaconTab): React.ReactNode => {
  switch (tab.kind) {
    case BeaconViewKind.HexEmbed:
      return <DashboardFrame dashboardKey={tab.key} />;
    case BeaconViewKind.Native: {
      const NativeView = nativeBeaconViews[tab.key];
      return NativeView ? <NativeView /> : null;
    }
    default:
      return assertNever(tab);
  }
};

// Hex embeds use a single-use iframe, so they stay mounted across tab switches to reuse it; native
// views source their own data and remount fresh.
const staysMountedWhileInactive = (tab: BeaconTab): boolean => tab.kind === BeaconViewKind.HexEmbed;

// `renderAll` keeps every panel mounted and renders the children we pass, handing us each tab's
// lifecycle: active tabs mount; Hex embeds also stay mounted once visited; native views render only
// while active and remount fresh on return.
const isTabContentMounted = (
  tab: BeaconTab,
  activeTabKey: string,
  visitedKeys: Set<string>
): boolean =>
  activeTabKey === tab.key || (staysMountedWhileInactive(tab) && visitedKeys.has(tab.key));

export const BeaconTabsView = ({ tabs, activeTabKey, onTabChange }: BeaconTabsViewProps) => {
  // activeTabKey is the effective key — the logic hook applies the first-tab default.
  const [visitedKeys, setVisitedKeys] = useState<Set<string>>(() => new Set([activeTabKey]));

  useEffect(() => {
    setVisitedKeys(prev => {
      if (prev.has(activeTabKey)) return prev;
      const next = new Set(prev);
      next.add(activeTabKey);
      return next;
    });
  }, [activeTabKey]);

  return (
    <div className="bg-white">
      <PageTitle className="pa4 ml2">Beacon</PageTitle>
      <Tabs
        activeKey={activeTabKey}
        onChange={({ activeKey }) => onTabChange(String(activeKey))}
        overrides={Overrides.Tabs}
        renderAll
      >
        {tabs.map(tab => (
          <Tab title={tab.displayName} key={tab.key} overrides={Overrides.Tab}>
            {isTabContentMounted(tab, activeTabKey, visitedKeys) ? renderTabContent(tab) : null}
          </Tab>
        ))}
      </Tabs>
    </div>
  );
};
